import { useEffect, useState } from 'react';
import { Box, Flex, Image } from '@chakra-ui/react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { AlarmClockIcon, CalendarOutlineIcon } from 'Asset/icon';
import { Condition } from 'Component/others';
import validateClockOut from 'Helper/validateClockOut';
import { useToast } from 'Store/toast';
import { useUserInfo } from 'Store/userInfo';
import ButtonSolid from 'UiKit/button/buttonSolid';
import InputTextAreaComponent from 'UiKit/input-textarea';
import InputSelectComponent from 'UiKit/input-select';
import { TextBold, TextMedium, TextRegular } from 'UiKit/typography';

import usePostClockInHost from 'Domain/host-attendance-record/usePostClockInHost';
import usePostClockOutHost from 'Domain/host-attendance-record/usePostClockOutHost';
import useGetWorkDateOptions from 'Domain/host-attendance-record/useGetWorkDateOptions';
import LoadingAttendance from 'View/host-attendance-record/components/loading-attendance';
import RetryButton from 'Component/retry-button';
import getUserLocation from 'View/host-attendance-record/helper/getUserLocation';
import { useHostAttendanceRecordState } from 'View/host-attendance-record/state';

import styles from './styles.module.css';

const FormAttendance = ({ presenceData }) => {
  const { t } = useTranslation('host-attendance');
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const { state, updateState } = useHostAttendanceRecordState();
  const { onOpenToast } = useToast();
  const { name } = useUserInfo();
  const { mutate: onPostClockInHost } = usePostClockInHost();
  const { mutate: onPostClockOutHost } = usePostClockOutHost();
  const { data: workDateOptions, isPending: isWorkDateOptionsPending } = useGetWorkDateOptions();

  const { isClockedIn, clockInTime, workDate } = presenceData ?? {};
  const isClockOutValid = isClockedIn && validateClockOut(clockInTime);

  const isSingleOption = workDateOptions?.length === 1;
  const isNoteRequired = isClockedIn && !isClockOutValid;

  const isNoteValid = !isNoteRequired || state.note?.trim().length >= 20;
  const isFormValid = !!(state.capturedImage && (isClockedIn || state.shiftDate) && isNoteValid);

  const isSubmitDisabled = !isFormValid;

  const handleNoteChange = (e) => {
    const value = e.target.value
      .trimStart()
      .replace('  ', ' ')
      .replace(/[^a-zA-Z0-9 ]/g, '');
    updateState({ note: value });
    if (!isClockedIn || isClockOutValid) return;
    const validNote = value.trimEnd();
    if (validNote.length < 20) {
      setError({
        message: validNote?.length !== value?.length ? t('preview_page.error_not_valid_text') : t('preview_page.error_text'),
      });
    } else {
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (state?.isLoading) return;
    updateState({ isLoading: true });

    const MAX_RETRIES = 10;
    let location = null;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      setRetryKey(attempt);
      [location] = await Promise.all([
        getUserLocation().catch(() => {
          return null;
        }),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);

      if (location) break;
    }

    if (!location) {
      updateState({ isLoading: false });
      onOpenToast({
        description: t('location_error_description'),
        status: 'failed',
        duration: 5,
        position: 'top',
        type: 'mobile',
      });
      return;
    }

    const { capturedImage, shiftDate, note } = state;
    const payload = {
      isFakeLocation: false,
      location,
      selfieImage: capturedImage?.first || null,
      locationImage: capturedImage?.second || null,
      workDate: shiftDate || null,
      note,
    };

    isClockedIn ? onPostClockOutHost(payload) : onPostClockInHost(payload);
  };

  useEffect(() => {
    if (isWorkDateOptionsPending) return;
    if (workDateOptions?.length === 1) {
      updateState({ shiftDate: workDateOptions[0]?.value });
    } else {
      updateState({ shiftDate: null });
    }
  }, [isWorkDateOptionsPending]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box className={styles['form-attendance__container']}>
      <Box className={styles['form-attendance__content']}>
        <Box className={styles['form-attendance__text']}>
          <TextBold className={styles['form-attendance__title']}>{name}</TextBold>
          <Flex gap="16px">
            <Condition statement={isClockedIn}>
              <Flex gap="8px">
                <CalendarOutlineIcon size={14} color="var(--color-neutral30)" />
                <TextMedium className={styles['form-attendance__subtitle']}>{moment(workDate).format('DD MMM YYYY')}</TextMedium>
              </Flex>
            </Condition>

            <Flex gap="8px">
              <AlarmClockIcon size={14} color="var(--color-neutral30)" />
              <TextMedium className={styles['form-attendance__subtitle']}>{moment().format('HH:mm')}</TextMedium>
            </Flex>
          </Flex>
        </Box>
        <Flex gap="20px">
          <Condition statement={state?.capturedImage?.first}>
            <Flex flexDir="column" align="center" gap="8px">
              <TextBold fontSize="var(--font-body2)">{t('preview_page.photo_1_label')}</TextBold>
              <Image src={state?.capturedImage?.first} alt="captured-attendance" className={styles['form-attendance__preview-image']} />
              <RetryButton onClick={() => updateState({ currentStep: 0, capturedImage: { first: null, second: state?.capturedImage?.second } })} />
            </Flex>
          </Condition>
          <Condition statement={state?.capturedImage?.second}>
            <Flex flexDir="column" align="center" gap="8px">
              <TextBold fontSize="var(--font-body2)">{t('preview_page.photo_2_label')}</TextBold>
              <Image src={state?.capturedImage?.second} alt="captured-attendance" className={styles['form-attendance__preview-image']} />
              <RetryButton onClick={() => updateState({ currentStep: 1, capturedImage: { first: state?.capturedImage?.first, second: null } })} />
            </Flex>
          </Condition>
        </Flex>

        <Condition statement={!isClockedIn}>
          <Flex gap="6px" direction="column" width="100%" marginTop="24px">
            <InputSelectComponent
              label={t('preview_page.shift_date')}
              placeholder={t('preview_page.shift_date_placeholder')}
              options={workDateOptions}
              className={styles['form-attendance__select-shift']}
              titleClassName={styles['form-attendance__select-shift--title']}
              field={{
                onChange: (selectedOption) => {
                  updateState({ shiftDate: selectedOption?.value });
                },
              }}
              value={isSingleOption ? workDateOptions[0] : workDateOptions?.find((o) => o.value === state?.shiftDate)}
              isReadOnly={isSingleOption}
              minHeight="34px"
              isRequired
            />
          </Flex>
        </Condition>

        <Box className={styles['form-textarea__container']}>
          <Flex className={styles['form-textarea__container--head']}>
            <TextBold className={styles['form-textarea__label']}>
              {t('preview_page.notes')}
              {isNoteRequired ? (
                <span style={{ color: 'var(--color-error50)' }}>*</span>
              ) : (
                <span className={styles['form-textarea__label--optional']}>{t('preview_page.optional')}</span>
              )}
            </TextBold>
            <TextRegular className={styles['form-textarea__count']}>({state?.note?.length}/100)</TextRegular>
          </Flex>
          <InputTextAreaComponent
            value={state?.note}
            className={styles['form-textarea__textarea']}
            placeholder={isClockedIn ? t('preview_page.clock_out_note_example') : t('preview_page.clock_in_note_example')}
            onChange={handleNoteChange}
            maxLength={100}
            error={error}
            isRequired={isNoteRequired}
          />
        </Box>
      </Box>
      <Box className={styles['form-attendance__footer']}>
        <ButtonSolid className={styles['form-attendance__button']} disabled={isSubmitDisabled} onClick={handleSubmit}>
          {t('common:submit')}
        </ButtonSolid>
      </Box>
      <Condition statement={state?.isLoading}>
        <LoadingAttendance key={retryKey} duration={3} />
      </Condition>
    </Box>
  );
};

export default FormAttendance;
